import React, { useState, useRef } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { SPEAKING_TOPICS, SPEAKING_VENUES } from "../../lib/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ============================================================
// SPEAKER REEL CONFIG — swap any time
// To use a YouTube video: set REEL.type = "youtube" and REEL.url to the YouTube URL
// To use an uploaded MP4: set REEL.type = "mp4" and REEL.url to the .mp4 URL
// ============================================================
const REEL = {
  type: "mp4", // "mp4" | "youtube"
  url: "https://customer-assets.emergentagent.com/job_crash-proof-movement/artifacts/600x9elk_Damien_Full%20Length_Final.mp4",
  poster: "/posters/reel.jpg",
  title: "Mr. Green Bay — Live",
  duration: "Full Length"
};

// Helper: extract YouTube ID from any common URL format
function getYouTubeId(url) {
  if (!url) return null;
  const m = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|v\/)|youtu\.be\/)([A-Za-z0-9_-]{11})/);
  return m ? m[1] : null;
}

const GALLERY = [
  "https://images.unsplash.com/photo-1475721027785-f74eccf877e2?w=900&auto=format&fit=crop",
  "https://images.unsplash.com/photo-1559223607-a43c990c692c?w=900&auto=format&fit=crop",
  "https://images.unsplash.com/photo-1542744095-291d1f67b221?w=900&auto=format&fit=crop",
  "https://images.unsplash.com/photo-1591115765373-5207764f72e7?w=900&auto=format&fit=crop"
];

function SpeakerReel() {
  const [playing, setPlaying] = useState(false);
  const videoRef = useRef(null);
  const ytId = REEL.type === "youtube" ? getYouTubeId(REEL.url) : null;

  const handlePlay = () => {
    setPlaying(true);
    if (REEL.type === "mp4" && videoRef.current) {
      videoRef.current.play().catch(() => {});
    }
  };

  return (
    <div className="group relative overflow-hidden rounded-3xl border border-white/5 bg-black">
      {/* Player */}
      {!playing && (
        <>
          <img
            src={REEL.poster}
            alt={REEL.title}
            className="aspect-video w-full object-cover transition-transform duration-700 group-hover:scale-105"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-ink-900 via-black/40 to-transparent" />
          <button
            data-testid="speaker-reel-play"
            onClick={handlePlay}
            className="absolute inset-0 flex items-center justify-center"
            aria-label="Play speaker reel"
          >
            <span className="flex h-20 w-20 items-center justify-center rounded-full border border-gold/50 bg-black/60 text-gold backdrop-blur-md transition-transform group-hover:scale-110 animate-pulse-gold">
              <i className="fa-solid fa-play text-2xl" />
            </span>
          </button>
          <div className="absolute bottom-5 left-6 right-6 flex items-end justify-between">
            <div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-gold">Speaker Reel</div>
              <div className="font-display text-2xl text-white">{REEL.title}</div>
            </div>
            <div className="hidden text-xs text-zinc-300 md:block">{REEL.duration}</div>
          </div>
        </>
      )}

      {playing && REEL.type === "mp4" && (
        <video
          ref={videoRef}
          src={REEL.url}
          poster={REEL.poster}
          controls
          autoPlay
          playsInline
          data-testid="speaker-reel-video"
          className="aspect-video w-full bg-black"
        />
      )}

      {playing && REEL.type === "youtube" && ytId && (
        <iframe
          src={`https://www.youtube.com/embed/${ytId}?autoplay=1&rel=0&modestbranding=1`}
          title={REEL.title}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          data-testid="speaker-reel-youtube"
          className="aspect-video w-full bg-black"
        />
      )}
    </div>
  );
}

export default function Speaking() {
  const [form, setForm] = useState({
    name: "", email: "", organization: "", event_type: "",
    event_date: "", location: "", audience_size: "", budget: "", message: ""
  });
  const [submitting, setSubmitting] = useState(false);

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error("Name and email are required.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/inquiries/speaking`, form);
      toast.success("Inquiry received. Damien's team will be in touch within 48 hours.");
      setForm({ name: "", email: "", organization: "", event_type: "", event_date: "", location: "", audience_size: "", budget: "", message: "" });
    } catch (err) {
      console.error(err);
      toast.error("Something went wrong. Try again or email info@mrgreenbay.com.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="speaking" data-testid="speaking-section" className="relative overflow-hidden bg-ink-900 py-28 md:py-36">
      <div className="mx-auto max-w-7xl px-6 md:px-12">
        <div className="mb-14 flex flex-col items-start justify-between gap-6 md:flex-row md:items-end">
          <div>
            <div className="mb-4 flex items-center gap-3 text-xs uppercase tracking-[0.3em] text-gold">
              <span className="h-px w-10 bg-gold/60" />
              Speaking & Events
            </div>
            <h2 className="font-display text-4xl font-bold leading-tight text-white md:text-5xl lg:text-6xl">
              Book Mr. Green Bay to
              <span className="block text-gradient-gold">move your room.</span>
            </h2>
          </div>
          <p className="max-w-md text-zinc-300">
            From middle school assemblies to executive leadership summits — Damien shows up
            ready, real, and unfiltered. Limited dates per quarter.
          </p>
        </div>

        <div className="grid gap-10 lg:grid-cols-12">
          {/* Speaker reel */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.8 }}
            className="lg:col-span-7"
          >
            <SpeakerReel />

            {/* Topics & venues */}
            <div className="mt-8 grid gap-6 sm:grid-cols-2">
              <div className="rounded-2xl border border-white/5 bg-ink-700/60 p-6">
                <div className="mb-3 text-xs uppercase tracking-[0.25em] text-gold">Signature Topics</div>
                <ul className="space-y-2 text-sm text-zinc-200">
                  {SPEAKING_TOPICS.map((t) => (
                    <li key={t} className="flex items-center gap-2">
                      <i className="fa-solid fa-diamond text-[7px] text-gold" /> {t}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="rounded-2xl border border-white/5 bg-ink-700/60 p-6">
                <div className="mb-3 text-xs uppercase tracking-[0.25em] text-gold">Stages We Serve</div>
                <ul className="space-y-2 text-sm text-zinc-200">
                  {SPEAKING_VENUES.map((t) => (
                    <li key={t} className="flex items-center gap-2">
                      <i className="fa-solid fa-diamond text-[7px] text-gold" /> {t}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Event gallery temporarily removed — will be added back when real event photos accumulate */}
          </motion.div>

          {/* Booking form */}
          <motion.form
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.8, delay: 0.1 }}
            onSubmit={onSubmit}
            data-testid="speaking-booking-form"
            className="glass-gold rounded-3xl p-7 md:p-9 lg:col-span-5"
          >
            <div className="mb-2 text-xs uppercase tracking-[0.3em] text-gold">Booking Inquiry</div>
            <div className="font-display text-2xl text-white md:text-3xl">Reserve a date.</div>
            <p className="mt-2 text-sm text-zinc-400">Tell us about your event. We respond within 48 hours.</p>

            <div className="mt-6 grid gap-4">
              <Field label="Your Name" name="name" value={form.name} onChange={onChange} testid="booking-name" required />
              <Field label="Email" name="email" type="email" value={form.email} onChange={onChange} testid="booking-email" required />
              <Field label="Organization / School" name="organization" value={form.organization} onChange={onChange} testid="booking-org" />
              <div className="grid grid-cols-2 gap-4">
                <Field label="Event Type" name="event_type" value={form.event_type} onChange={onChange} testid="booking-event-type" placeholder="Keynote, Assembly, Workshop…" />
                <Field label="Event Date" name="event_date" value={form.event_date} onChange={onChange} testid="booking-event-date" placeholder="MM/DD/YYYY or Q1 2026" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <Field label="Location" name="location" value={form.location} onChange={onChange} testid="booking-location" />
                <Field label="Audience Size" name="audience_size" value={form.audience_size} onChange={onChange} testid="booking-audience" />
              </div>
              <Field label="Budget Range" name="budget" value={form.budget} onChange={onChange} testid="booking-budget" />
              <div>
                <label className="mb-1.5 block text-[10px] uppercase tracking-[0.25em] text-zinc-500">Message</label>
                <textarea
                  data-testid="booking-message"
                  name="message"
                  value={form.message}
                  onChange={onChange}
                  rows={4}
                  className="w-full rounded-xl border border-white/10 bg-black/40 px-4 py-3 text-sm text-white placeholder-zinc-600 outline-none transition-colors focus:border-gold/60 focus:ring-2 focus:ring-gold/20"
                  placeholder="Share details about your audience and what you'd love Damien to cover."
                />
              </div>

              <button
                type="submit"
                disabled={submitting}
                data-testid="booking-submit"
                className="btn-gold mt-2 inline-flex items-center justify-center rounded-full px-7 py-4 text-sm font-semibold uppercase tracking-wider disabled:opacity-60"
              >
                {submitting ? (
                  <><i className="fa-solid fa-circle-notch fa-spin mr-2" /> Sending</>
                ) : (
                  <><i className="fa-solid fa-paper-plane mr-2" /> Send Booking Inquiry</>
                )}
              </button>
            </div>
          </motion.form>
        </div>
      </div>
    </section>
  );
}

function Field({ label, name, value, onChange, testid, type = "text", required = false, placeholder = "" }) {
  return (
    <div>
      <label className="mb-1.5 block text-[10px] uppercase tracking-[0.25em] text-zinc-500">{label}{required && " *"}</label>
      <input
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        required={required}
        placeholder={placeholder}
        data-testid={testid}
        className="w-full rounded-xl border border-white/10 bg-black/40 px-4 py-3 text-sm text-white placeholder-zinc-600 outline-none transition-colors focus:border-gold/60 focus:ring-2 focus:ring-gold/20"
      />
    </div>
  );
}
